import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  Inject,
  PLATFORM_ID,
  AfterViewInit,
} from '@angular/core';
import { StreappEvent } from '../../../models/streappevent.model';
import { StreappEventsService } from '../../../services/streapp-events.service';
import {
  ActivatedRoute,
  Router,
} from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AuthService } from 'src/app/services/auth.service';
import { MediaItem, MediaType } from 'src/app/models/mediaitem.model';
import { ReactionService } from '../../../services/reaction.service';
import { StreappReaction } from '../../../models/streappreaction.model';
import { MatDialog } from '@angular/material/dialog';
import { HttpErrorResponse } from '@angular/common/http';
import { HelpBoxService } from 'src/app/services/help-box.service';
import { TranslateService } from '@ngx-translate/core';
import { DeviceCheckUtility, DeviceOS } from 'src/app/utilities/device-check';
import { SimpleTextDialogData } from 'src/app/components/simple-text-dialog/models/simple-text-dialog-data.model';
import { SimpleTextDialogComponent } from 'src/app/components/simple-text-dialog/simple-text-dialog.component';
import { NavigationService } from 'src/app/services/navigation-end.service';
import { Meta, Title } from '@angular/platform-browser';
import { isPlatformServer } from '@angular/common';
import { RobotsService } from 'src/app/services/robots.service';
import { isPlatformBrowser } from '@angular/common';
import { DeviceService } from '../../../services/device.service';
import { StreApperInfo } from 'src/app/models/streapper-info.model';

@Component({
  selector: 'app-detail-page',
  templateUrl: './detail-page.component.html',
  styleUrls: ['./detail-page.component.scss'],
  standalone: false
})
export class DetailPageComponent implements OnInit, OnDestroy, AfterViewInit {
  isServer: boolean = false;
  event: StreappEvent;
  reactions: StreappReaction[];
  eventId: string;
  loading: boolean = true;
  error: boolean = false;
  selectedMediaItem: MediaItem;
  hasScrolled: boolean = false;
  loggedIn: boolean;
  fromApp: boolean = false;
  selectedItem: number = 0;
  selectedItemIndex: number = 0;
  isMobile: boolean = false;
  isMyStreApp: boolean = false;
  showLinkedEvents: boolean = true;
  streapper: StreApperInfo;

  displayAds: boolean = true;

  private readonly destroy$: Subject<boolean> = new Subject();

  constructor(
    private readonly streappService: StreappEventsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly navigation: NavigationService,
    private readonly authService: AuthService,
    private readonly reactionService: ReactionService,
    private readonly dialog: MatDialog,
    private readonly helpBox: HelpBoxService,
    private readonly translate: TranslateService,
    private readonly meta: Meta,
    private readonly robots: RobotsService,
    private titleService: Title,
    private readonly deviceService: DeviceService,
    @Inject(PLATFORM_ID) protected _platformId: Object
  ) {
    this.isServer = isPlatformServer(_platformId);
  }

  MediaType = MediaType;

  @HostListener('window:scroll', []) onScroll(): void {
    if (isPlatformBrowser(this._platformId)) {
      this.hasScrolled = window.pageYOffset > 0;
    }
  }

  async ngOnInit(): Promise<void> {
    this.robots.setRobots();
    this.loggedIn = this.authService.isAuthenticated();
    await this.getEventIdAndLoadEvent();
    this.deviceService.isMobileView.subscribe((value) => {
      this.isMobile = value;
    });

    this.displayAds = !this.authService.isAdFree;
  }

  ngAfterViewInit() {
    if (!this.displayAds) return;

    if (isPlatformBrowser(this._platformId)) {
      setTimeout(() => {
        try {
          (window["adsbygoogle"] = window["adsbygoogle"] || []).push({});
        }
        catch (e) {
          console.error("ads", e);
        }
      }, 900);
    }
  }

  public checkOverflow(element): boolean {
    if (!isPlatformBrowser(this._platformId) || !element) {
      return false; // Safe default for SSR
    }

    return element.offsetHeight < element.scrollHeight || element.offsetWidth < element.scrollWidth;
  }

  isAdultOrLowImpact(): boolean {
    if (!this.event.isHighImpactEvent) {
      return true;
    }

    if (!this.authService.isAuthenticated()) {
      return false;
    }

    const isEventOwnedByUser = this.authService.userId === this.event.createdBy;
    return this.authService.isAdult || isEventOwnedByUser;
  }

  ngOnDestroy(): void {
    this.meta.updateTag({
      name: 'description',
      content:
        "Een online platform dat gebruikers in staat stelt om ooggetuige video's te delen en te bekijken",
    });
    this.meta.removeTag('name="og:description"');
    this.meta.removeTag('name="og:title"');
    this.meta.removeTag('name="og:image"');
    this.meta.removeTag('name="twitter:card"');
    this.meta.removeTag('name="summary_large_image"');
    this.titleService.setTitle('StreApp');

    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

  async getEventIdAndLoadEvent(): Promise<void> {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(async (params) => {
      let id = params['id'];
      if (!this.isServer) {
        if (this.route.snapshot.queryParamMap.has('app')) {
          if (
            DeviceCheckUtility.getMobileOperatingSystem(this._platformId) == DeviceOS.iOS ||
            DeviceCheckUtility.getMobileOperatingSystem(this._platformId) == DeviceOS.Android
          ) {
            if (DeviceCheckUtility.getMobileOperatingSystem(this._platformId) == DeviceOS.iOS) {
              this.helpBox.openOpenApp('streapp://' + id, DeviceOS.iOS);
            }
            if (
              DeviceCheckUtility.getMobileOperatingSystem(this._platformId) == DeviceOS.Android
            ) {
              this.helpBox.openOpenApp(
                'intent://' +
                id +
                '/#Intent;package=nl.streapp;scheme=streapp;S.browser_fallback_url=https%3A%2F%2Fplay.google.com%2Fstore%2Fapps%2Fdetails%3Fid%3Dnl.streapp;end',
                DeviceOS.Android
              );
            }
          }
        }
      }
      await this.loadEventFromService(id);
    });
  }

  async loadEventFromService(id: string): Promise<void> {
    this.loading = true;

    try {
      const data: StreappEvent = await this.streappService
        .getEventById(id, this.authService.authorizationHeaderValue);

      if (!data || (data.deleted && !this.authService.isAdmin)) {
        // Either event doesn't exists or user is underage and its an highimpact event
        this.router.navigate(['/']);
      }

      this.event = data;
      await this.loadStreapperInfo(data);
      await this.loadReactions();
      this.selectedMediaItem = this.event.mediaItems[0];

      this.meta.updateTag({
        name: 'description',
        content: data.description,
      });
      this.meta.addTag({
        name: 'og:description',
        content: data.description,
      });
      this.meta.addTag({
        name: 'og:title',
        content: data.title,
      });
      this.meta.addTag({
        name: 'og:image',
        content: data.thumbnail,
      });
      this.meta.addTag({
        name: 'twitter:card',
        content: 'summary_large_image',
      });

      this.titleService.setTitle('StreApp - ' + data.title);
      this.isMyStreApp =
        this.authService.isAuthenticated() &&
        this.event.createdBy == this.authService.userId;
      this.loading = false;

    }
    catch (error) {
      if (error instanceof HttpErrorResponse) {
        if (error.status == 401 && isPlatformBrowser(this._platformId)) {
          this.loginForEvent();
          return;
        }
        this.loading = false;
        this.error = true;
      }
    };
  }

  selectMediaItem(item: MediaItem): void {
    this.selectedMediaItem = item;

    if (this.selectedItem === 0) {
      this.selectedItem = 1;
    } else {
      this.selectedItem = 0;
    }
    this.selectedItemIndex = this.event.mediaItems.findIndex(mediaItem => mediaItem.partitionKey === item.partitionKey);
  }

  navigate(direction: number) {
    const newIndex = this.event.mediaItems.findIndex(mediaItem => mediaItem.partitionKey === this.selectedMediaItem.partitionKey) + direction;

    if (newIndex >= 0 && newIndex < this.event.mediaItems.length) {
      if (this.selectedItem === 0) {
        this.selectedItem = 1;
      } else {
        this.selectedItem = 0;
      }
      this.selectedMediaItem = this.event.mediaItems[newIndex];
      this.selectedItemIndex = newIndex;
    }
  }

  loginForEvent() {
    if (!this.authService.isAuthenticated()) {
      // Adult Content popup
      this.dialog
        .open<SimpleTextDialogComponent, SimpleTextDialogData>(
          SimpleTextDialogComponent,
          {
            data: {
              title: 'overview.login.popup_title',
              text: 'overview.login.popup_text',
              showButton: true,
              showCancelButton: true,
              buttonCancelText: 'overview.login.popup_cancel',
              buttonText: 'overview.login.popup_button',
            },
          }
        )
        .afterClosed()
        .subscribe((data: boolean) => {
          if (data) {
            // Redirect to login page with current url to redirect back after login.
            this.router.navigate(['/login'], {
              queryParams: { redirect: this.router.url },
              replaceUrl: true,
            });
          } else {
            //Redirect to last page or the main page if opened in a new tab when cancelled
            this.navigation.back();
          }
        });
    } else {
      // User is logged in, but it's high impact
      this.router.navigate(['/']);
    }
  }

  async loadStreapperInfo(event: StreappEvent): Promise<void> {
    const data: StreApperInfo = await this.streappService.getStreapperInfoId(event.createdBy);
    this.streapper = data;
  }

  async loadReactions(): Promise<void> {
    const data: StreappReaction[] = await this.reactionService
      .getReactionsForEvent(
        this.event.linkedToMainStreappID
          ? this.event.linkedToMainStreappID
          : this.event.partitionKey
      );
    this.reactions = data;
  }

  addReaction(reactionText: string): void {
    this.reactionService
      .addReaction(
        this.event.linkedToMainStreappID
          ? this.event.linkedToMainStreappID
          : this.event.partitionKey,
        reactionText,
        this.authService.authorizationHeaderValue
      )
      .then((data: StreappReaction) => {
        this.reactions.unshift(data);
      })
      .catch((err) => {
        console.error(err);
      });
  }

  updateReaction(reactionText: { text: string; id: string }): void {
    this.reactionService
      .updateReaction(
        this.event.linkedToMainStreappID
          ? this.event.linkedToMainStreappID
          : this.event.partitionKey,
        reactionText.id,
        reactionText.text,
        this.authService.authorizationHeaderValue
      )
      .then(() => {
        this.loadReactions();
      })
      .catch((err) => {
        console.error(err);
      });
  }

  removeReaction(reactionId: string): void {
    this.reactionService
      .deleteReaction(
        this.event.linkedToMainStreappID
          ? this.event.linkedToMainStreappID
          : this.event.partitionKey,
        reactionId,
        this.authService.authorizationHeaderValue
      )
      .then(() => {
        this.loadReactions();
      });
  }

  isLoggedIn(): boolean {
    return this.authService.isAuthenticated();
  }

  dialogOpen() {
    this.translate.get('help.new-event.disabled').subscribe((text) => {
      this.helpBox.openBox(text);
    });
  }

  getThumbnailUrl(item: string) {
    if (item.indexOf('streapp-processing.png') > -1) {
      return '';
    }
    return item;
  }

  updateShowLinkedEvents(show: boolean): void {
    this.showLinkedEvents = show;
  }
}
